if (!customElements.get('site-header')) {
  customElements.define(
    'site-header',
    class SiteHeader extends HTMLElement {
      connectedCallback() {
        this.searchToggle = this.querySelector('[data-search-toggle]');
        this.searchPanel = this.querySelector('[data-search-panel]');
        this.drawer = this.querySelector('[data-drawer]');
        this.drawerToggle = this.querySelector('[data-drawer-open]');
        this.canHover = window.matchMedia('(hover: hover)');

        this.onClick = this.onClick.bind(this);
        this.onKeyUp = this.onKeyUp.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onBlockSelect = this.onBlockSelect.bind(this);
        this.setHeight = this.setHeight.bind(this);
        this.fitResults = this.fitResults.bind(this);

        this.addEventListener('click', this.onClick);
        this.addEventListener('keyup', this.onKeyUp);
        this.addEventListener('focusout', (event) => {
          if (!event.target.closest('.site-header__menu-item')) return;
          if (!event.relatedTarget || !event.target.closest('.site-header__menu-item').contains(event.relatedTarget)) {
            this.closeDropdowns();
          }
        });
        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('shopify:block:select', this.onBlockSelect);

        this.querySelectorAll('.site-header__menu-item').forEach((item) => {
          const toggle = item.querySelector('[data-dropdown-toggle]');
          if (!toggle) return;
          item.addEventListener('mouseenter', () => this.canHover.matches && this.openDropdown(toggle));
          item.addEventListener('mouseleave', () => this.canHover.matches && this.scheduleClose());
        });

        this.drawer?.addEventListener('close', () => this.onDrawerClose());
        this.drawer?.addEventListener('click', (event) => {
          if (event.target === this.drawer) this.drawer.close();
        });

        this.watchScroll();
        this.setHeight();
        window.addEventListener('resize', this.setHeight);
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
        window.removeEventListener('resize', this.setHeight);
        this.scrollObserver?.disconnect();
        this.sentinel?.remove();
        document.documentElement.classList.remove('site-header-locked');
      }

      onClick(event) {
        const dropdownToggle = event.target.closest('[data-dropdown-toggle]');
        if (dropdownToggle) {
          const isOpen = dropdownToggle.getAttribute('aria-expanded') === 'true';
          isOpen && !this.canHover.matches ? this.closeDropdowns() : this.openDropdown(dropdownToggle);
          return;
        }

        if (event.target.closest('[data-search-toggle]')) {
          this.searchPanel.hidden ? this.openSearch() : this.closeSearch();
          return;
        }

        if (event.target.closest('[data-search-close]')) {
          this.closeSearch(true);
          return;
        }

        if (event.target.closest('[data-drawer-open]')) {
          this.openDrawer();
          return;
        }

        if (event.target.closest('[data-drawer-close]')) this.drawer.close();
      }

      onKeyUp(event) {
        if (event.key !== 'Escape') return;

        const openToggle = this.querySelector('[data-dropdown-toggle][aria-expanded="true"]');
        if (openToggle) {
          this.closeDropdowns();
          openToggle.focus();
        }

        if (!this.searchPanel.hidden) this.closeSearch(true);
      }

      onDocumentClick(event) {
        if (this.contains(event.target)) {
          if (!event.target.closest('.site-header__menu-item')) this.closeDropdowns();
          return;
        }
        this.closeDropdowns();
        if (!this.searchPanel.hidden) this.closeSearch();
      }

      onBlockSelect(event) {
        if (!this.contains(event.target)) return;
        const panel = event.target.closest('[data-dropdown-panel]');
        if (panel) this.openDropdown(this.querySelector(`[aria-controls="${panel.id}"]`));
      }

      openDropdown(toggle) {
        clearTimeout(this.closeTimer);
        this.closeSearch();
        this.querySelectorAll('[data-dropdown-toggle]').forEach((other) => {
          if (other !== toggle) this.setDropdown(other, false);
        });
        this.setDropdown(toggle, true);
      }

      scheduleClose() {
        clearTimeout(this.closeTimer);
        this.closeTimer = setTimeout(() => this.closeDropdowns(), 180);
      }

      closeDropdowns() {
        clearTimeout(this.closeTimer);
        this.querySelectorAll('[data-dropdown-toggle]').forEach((toggle) => this.setDropdown(toggle, false));
      }

      setDropdown(toggle, open) {
        const panel = document.getElementById(toggle.getAttribute('aria-controls'));
        toggle.setAttribute('aria-expanded', open);
        if (panel) panel.hidden = !open;
      }

      openSearch() {
        this.closeDropdowns();
        this.searchPanel.hidden = false;
        this.fitResults();
        window.visualViewport?.addEventListener('resize', this.fitResults);
        window.visualViewport?.addEventListener('scroll', this.fitResults);
        this.searchToggle.setAttribute('aria-expanded', 'true');
        const input = this.searchPanel.querySelector('input[type="search"]');
        input.focus();
        input.setSelectionRange(input.value.length, input.value.length);
      }

      closeSearch(returnFocus = false) {
        if (!this.searchPanel || this.searchPanel.hidden) return;
        this.searchPanel.querySelector('predictive-search')?.close?.();
        this.searchPanel.hidden = true;
        this.searchToggle.setAttribute('aria-expanded', 'false');
        window.visualViewport?.removeEventListener('resize', this.fitResults);
        window.visualViewport?.removeEventListener('scroll', this.fitResults);
        if (returnFocus) this.searchToggle.focus();
      }

      fitResults() {
        const results = this.searchPanel.querySelector('.site-header__results');
        if (!results) return;

        const viewport = window.visualViewport;
        const visibleBottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;
        const top = this.searchPanel.querySelector('.site-header__search-form').getBoundingClientRect().bottom + 8;
        results.style.setProperty('--site-search-max-height', `${Math.max(visibleBottom - top - 12, 160)}px`);
      }

      openDrawer() {
        this.closeSearch();
        this.drawer.showModal();
        this.drawer.focus();
        this.drawerToggle.setAttribute('aria-expanded', 'true');
        document.documentElement.classList.add('site-header-locked');
      }

      onDrawerClose() {
        this.drawerToggle.setAttribute('aria-expanded', 'false');
        this.drawerToggle.focus();
        document.documentElement.classList.remove('site-header-locked');
      }

      watchScroll() {
        if (this.dataset.sticky !== 'true') return;

        this.sentinel = document.createElement('div');
        this.sentinel.setAttribute('aria-hidden', 'true');
        this.closest('.section-header')?.before(this.sentinel);
        this.scrollObserver = new IntersectionObserver(([entry]) => {
          this.classList.toggle('is-scrolled', !entry.isIntersecting);
        });
        this.scrollObserver.observe(this.sentinel);
      }

      setHeight() {
        document.documentElement.style.setProperty('--header-height', `${this.offsetHeight}px`);
      }
    }
  );
}
